import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { catchError, from, map, Observable, of, switchMap, throwError, Subject } from 'rxjs';
import { AuthService } from './auth.service';
import { CustomNotifierService } from 'src/app/core/services/custom-notifier.service';
import { Router } from '@angular/router';
import { environment } from 'src/environments/environment';
import { AuthUtils } from './auth.utils';
import { AppConstants } from '../constants/constants';

interface BackendErrorResponse {
    errorCode?: string;
    errorMsgEn?: string;
    errorMsgAr?: string;
    timestamp?: string;
}

type ErrorCategory = 'network' | 'auth' | 'forbidden' | 'validation' | 'server' | 'timeout' | 'unknown';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
    private readonly destroy$ = new Subject<void>();
    private readonly ERROR_MESSAGES = {
        en: {
            network: 'Connection lost. Please check your internet and try again.',
            unauthorized: 'Unauthorized access. You do not have access to this resource.',
            sessionExpired: 'Your session has expired. Please sign in again.',
            forbidden: 'Access denied. You do not have permission to access this resource.',
            notFound: 'The requested information could not be found.',
            timeout: 'Request timed out. Please check your connection and try again.',
            serverError: 'Service temporarily unavailable. Please try again in a moment.',
            default: 'Something went wrong. Please try again or contact support if the problem persists.'
        },
        ar: {
            network: 'انقطع الاتصال. يرجى التحقق من الإنترنت والمحاولة مرة أخرى.',
            unauthorized: 'غير مصرح بالوصول إلى هذا المورد.',
            sessionExpired: 'انتهت صلاحية جلستك. يرجى تسجيل الدخول مرة أخرى.',
            forbidden: 'تم رفض الوصول. ليس لديك إذن للوصول إلى هذا المورد.',
            notFound: 'لم يتم العثور على المعلومات المطلوبة.',
            timeout: 'انتهت مهلة الطلب. يرجى التحقق من الاتصال والمحاولة مرة أخرى.',
            serverError: 'الخدمة غير متاحة مؤقتاً. يرجى المحاولة بعد قليل.',
            default: 'حدث خطأ ما. يرجى المحاولة مرة أخرى أو الاتصال بالدعم في حالة استمرار المشكلة.'
        }
    };

    constructor(
        private readonly _authService: AuthService,
        private readonly _customNotifierService: CustomNotifierService,
        private readonly _router: Router
    ) { }

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        const modifiedReq = this.addHeaders(req);

        return next.handle(modifiedReq).pipe(
            catchError((error: any) => {
                // Blob endpoints (e.g. profile photos) still receive JSON API errors.
                // Decode for classification only; callers retain the original error.
                if (error instanceof HttpErrorResponse && error.error instanceof Blob && error.error.size > 0) {
                    return from(error.error.text()).pipe(
                        map(body => {
                            try {
                                const parsed: unknown = JSON.parse(body);
                                if (!parsed || typeof parsed !== 'object') return error;
                                return new HttpErrorResponse({
                                    error: parsed, headers: error.headers,
                                    status: error.status, url: error.url
                                });
                            } catch {
                                return error;
                            }
                        }),
                        catchError(() => of(error)),
                        switchMap(decoded => this.handleError(decoded, modifiedReq, error))
                    );
                }
                return this.handleError(error, modifiedReq);
            })
        );
    }

    private addHeaders(req: HttpRequest<any>): HttpRequest<any> {
        // Skip auth headers for external APIs
        if (!this.shouldApplySpecialHandling(req)) {
            return req;
        }

        const headers: { [key: string]: string } = {
            'sourceChannel': 'WEB_PORTAL'
        };

        // Add Content-Type for JSON requests
        if (
            req.body &&
            typeof req.body === 'object' &&
            !(req.body instanceof FormData) &&
            !req.headers.has('Content-Type')
        ) {
            headers['Content-Type'] = 'application/json';
        }


        // Only add Authorization header if token exists and not refresh token request
        if (this._authService.accessToken && !req.url.includes('/refresh-token')) {
            headers['Authorization'] = `Bearer ${this._authService.accessToken}`;
        }

        return req.clone({ setHeaders: headers });
    }

    private handleError(error: any, req: HttpRequest<any>, originalError = error): Observable<never> {
        // Handle specific error responses that should be passed through
        if (this.shouldPassThroughError(error)) {
            return throwError(() => originalError);
        }

        const requestToken = this.isSessionRequest(req) ? this.bearerToken(req) : null;
        const unauthorized = this.isUnauthorizedError(error);
        // Ignore stale authentication failures after logout or a newer login.
        // Propagate the original error without clearing or notifying the new session.
        if (unauthorized && requestToken && requestToken !== this._authService.accessToken) {
            return throwError(() => originalError);
        }
        const expiresAt = requestToken ? AuthUtils.getTokenExpirationTime(requestToken) : null;
        const resourceDenied = error.error?.errorCode === '403_UNAUTHORIZED_RS';
        const sessionExpired = unauthorized && !resourceDenied && expiresAt !== null && expiresAt <= Date.now();
        // The backend uses an empty 401 for rejected credentials, including revoked
        // tokens whose exp claim is still in the future. Never infer this from {}.
        const invalidSession = unauthorized && !!requestToken && this.hasEmptyBody(error);
        const language = this.getCurrentLanguage();
        const errorMessage = this.getErrorMessage(error, language, sessionExpired || invalidSession);

        if (sessionExpired || invalidSession) {
            this.handleUnauthorizedAccess();
        }

        // Handle OTP exceed tries - redirect to sign-in
        if (this.isOtpExceedTriesError(error)) {
            this.handleOtpExceedTries(errorMessage);
            return throwError(() => originalError);
        }

        // Show user-friendly notification for user-facing errors
        const errorCategory = this.getErrorCategory(error);
        if (errorCategory !== 'validation' || this.shouldShowValidationError(error)) {
            this._customNotifierService.notify('error', errorMessage);
        }

        return throwError(() => originalError);
    }

    private getCurrentLanguage(): 'en' | 'ar' {
        return localStorage.getItem('language') === 'en' ? 'en' : 'ar';
    }

    private getErrorMessage(error: any, language: 'en' | 'ar', sessionExpired = false): string {
        const messages = this.ERROR_MESSAGES[language];
        if (sessionExpired) return messages.sessionExpired;

        // Check for custom backend error message first
        if (this.isBackendError(error)) {
            const backendError = error.error;
            if (backendError.errorMsgEn || backendError.errorMsgAr) {
                return language === 'en' ? backendError.errorMsgEn : backendError.errorMsgAr;
            }
        }

        const errorCategory = this.getErrorCategory(error);
        switch (errorCategory) {
            case 'network':
                return messages.network;
            case 'auth':
                return messages.unauthorized;
            case 'forbidden':
                return messages.forbidden;
            case 'timeout':
                return messages.timeout;
            case 'server':
                return messages.serverError;
            case 'validation':
            case 'unknown':
            default:
                return messages.default;
        }
    }

    private shouldPassThroughError(error: any): boolean {
        if (!(error instanceof HttpErrorResponse)) {
            return false;
        }

        // Pass through specific status codes that should be handled by components
        const passThroughCodes = [422, 428]; // Validation errors handled by components

        // Pass through account locked errors to be handled by signin component (no notification here)
        if (error.status === 401 && error.error?.errorCode === '401_ACCOUNT_LOCKED') {
            return true;
        }

        // Pass through manual bank statement permission errors to be handled by the calling component
        // (not a session/auth failure - should not log the user out)
        if (error.status === 401 && error.error?.errorCode === '401_UNAUTHORIZED_MANUAL_BANK_STATEMENT') {
            return true;
        }

        return passThroughCodes.includes(error.status);
    }

    private isUnauthorizedError(error: any): boolean {
        return error instanceof HttpErrorResponse &&
            error.status === 401 &&
            error.error?.errorCode !== '401_ACCOUNT_LOCKED' &&
            error.error?.errorCode !== '401_UNAUTHORIZED_MANUAL_BANK_STATEMENT';
    }

    private handleUnauthorizedAccess(): void {
        // Synchronous cleanup also makes other in-flight responses stale (see above).
        this._authService.clearLocalAuthState();

        // Navigate to sign-in page
        void this._router.navigate([AppConstants.ROUTES.SIGN_IN], {
            queryParams: { returnUrl: this._router.url }
        });
    }

    private isOtpExceedTriesError(error: any): boolean {
        return error instanceof HttpErrorResponse &&
            error.status === 400 &&
            error.error?.errorCode === '400_OTP_EXCEED_TRIES';
    }

    private handleOtpExceedTries(errorMessage: string): void {
        // Show the error notification
        this._customNotifierService.notify('error', errorMessage);

        // Navigate to sign-in page (no signOut needed - user isn't authenticated yet during OTP verification)
        void this._router.navigate([AppConstants.ROUTES.SIGN_IN]);
    }

    private shouldApplySpecialHandling(req: HttpRequest<any>): boolean {
        const base = environment.baseURL.replace(/\/$/, '');
        return req.url === base || req.url.startsWith(`${base}/`) || req.url.startsWith(`${base}?`);
    }

    private bearerToken(req: HttpRequest<any>): string | null {
        return /^Bearer\s+(.+)$/i.exec(req.headers.get('Authorization') ?? '')?.[1] ?? null;
    }

    private isSessionRequest(req: HttpRequest<any>): boolean {
        const url = req.url.split(/[?#]/)[0].replace(/\/$/, '');
        const usersPath = url.slice(AppConstants.API.USERS_API.length);
        return this.shouldApplySpecialHandling(req) &&
            !url.endsWith('/refresh-token') &&
            // Public authentication/registration/OTP and invitation requests do
            // not validate the saved access token, even if one is attached.
            !(url.startsWith(`${AppConstants.API.USERS_API}/`) &&
                /^\/(auth|logout|registration|forgotPassword|resetPassword)(\/|$)/.test(usersPath)) &&
            !(url.startsWith(`${AppConstants.API.USERS_API}/`) && /^\/[^/]+\/otp(\/|$)/.test(usersPath)) &&
            !url.startsWith(`${environment.baseURL}/aid/`) &&
            !(url === AppConstants.API.BUSINESS_ACCOUNT_USERS && req.params.has('token')) &&
            !(url === AppConstants.API.BUSINESS_ACCOUNT_USERS && /[?&]token=/.test(req.url));
    }

    private hasEmptyBody(error: HttpErrorResponse): boolean {
        const body = error.error;
        return body === null || body === undefined || body === '' ||
            (body instanceof Blob && body.size === 0) ||
            (body instanceof ArrayBuffer && body.byteLength === 0);
    }

    private getErrorCategory(error: HttpErrorResponse): ErrorCategory {
        if (error.error instanceof ErrorEvent || error.status === 0) return 'network';
        if (error.status === 401) return 'auth';
        if (error.status === 403) return 'forbidden';
        if ([400, 422, 428].includes(error.status)) return 'validation';
        if ([500, 502, 503, 504].includes(error.status)) return 'server';
        if ([408, 504].includes(error.status)) return 'timeout';
        return 'unknown';
    }

    private isBackendError(error: any): error is HttpErrorResponse & { error: BackendErrorResponse } {
        return error instanceof HttpErrorResponse &&
            error.error &&
            typeof error.error === 'object';
    }

    private shouldShowValidationError(error: any): boolean {
        if (!(error instanceof HttpErrorResponse)) {
            return false;
        }

        // Show validation errors that have backend error messages (like 400_OTP_INV)
        if (error.status === 400 && error.error?.errorMsgEn && error.error?.errorMsgAr) {
            return true;
        }

        return false;
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
